import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { UserCheck, Search, CheckCircle2, XCircle, Clock4 } from "lucide-react";

const STATUS_COLORS = {
  present: "bg-emerald-100 text-emerald-700",
  late: "bg-[#D4AF37]/15 text-[#0A192F]",
  absent: "bg-red-100 text-red-700",
  excused: "bg-slate-100 text-slate-600",
};

export default function AttendancePage() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [regs, setRegs] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/v2/events?limit=20")
      .then((r) => {
        setEvents(r.data.items || []);
        if (r.data.items?.[0]) setEventId(r.data.items[0].id);
      })
      .catch((err) => {
        if (err.response?.status !== 403) {
          toast.error("Could not load events");
        }
      });
  }, []);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/v2/sessions?event_id=${eventId}`)
      .then((r) => {
        setSessions(r.data.items || []);
        if (r.data.items?.[0]) setSessionId(r.data.items[0].id);
        else setSessionId("");
      })
      .catch(() => setSessions([]));
    api.get(`/v2/registrations?event_id=${eventId}&limit=200`)
      .then((r) => setRegs(r.data.items || []))
      .catch(() => setRegs([]));
  }, [eventId]);

  useEffect(() => {
    if (!sessionId) { setAttendanceMap({}); return; }
    api.get(`/v2/attendance?session_id=${sessionId}&limit=500`)
      .then((r) => {
        const map = {};
        (r.data.items || []).forEach((a) => { map[a.delegate_id] = a.status; });
        setAttendanceMap(map);
      })
      .catch(() => setAttendanceMap({}));
  }, [sessionId]);

  const mark = async (delegate_id, status) => {
    try {
      await api.post("/v2/attendance", { session_id: sessionId, delegate_id, status });
      setAttendanceMap((m) => ({ ...m, [delegate_id]: status }));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = regs.filter((r) =>
    !search || r.delegate_name?.toLowerCase().includes(search.toLowerCase())
            || r.delegate_email?.toLowerCase().includes(search.toLowerCase())
  );

  const counts = Object.values(attendanceMap).reduce((acc, s) => {
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Roll call
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Attendance
            </h1>
          </div>
          <div className="flex gap-2">
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger className="w-[220px] h-11" data-testid="att-event-select"><SelectValue placeholder="Event" /></SelectTrigger>
              <SelectContent>
                {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sessionId} onValueChange={setSessionId}>
              <SelectTrigger className="w-[260px] h-11" data-testid="att-session-select"><SelectValue placeholder="Session" /></SelectTrigger>
              <SelectContent>
                {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.agenda}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 grid grid-cols-4 gap-3">
          {[
            { k: "present", label: "Present", Icon: CheckCircle2, color: "emerald" },
            { k: "late", label: "Late", Icon: Clock4, color: "amber" },
            { k: "absent", label: "Absent", Icon: XCircle, color: "red" },
            { k: "excused", label: "Excused", Icon: UserCheck, color: "slate" },
          ].map(({ k, label, Icon }) => (
            <div key={k} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center justify-between" data-testid={`count-${k}`}>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
                <p className="mt-1 font-serif text-2xl text-[#0A192F]">{counts[k] || 0}</p>
              </div>
              <Icon className="h-5 w-5 text-[#D4AF37]" />
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="mt-6 relative max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Find delegate…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-11"
            data-testid="att-search-input"
          />
        </div>

        {/* Delegate roster with status toggles */}
        <div className="mt-6 rounded-xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Delegate</th>
                <th className="text-left px-5 py-3">Current status</th>
                <th className="text-left px-5 py-3">Mark</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const status = attendanceMap[r.delegate_id];
                return (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`att-row-${r.delegate_id}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-[#0A192F]">{r.delegate_name}</p>
                      <p className="text-xs text-slate-500">{r.delegate_email}</p>
                    </td>
                    <td className="px-5 py-3">
                      {status ? (
                        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[status]}`}>
                          {status}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Not marked</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1">
                        {["present", "late", "absent", "excused"].map((s) => (
                          <Button
                            key={s}
                            size="sm"
                            variant={status === s ? "default" : "outline"}
                            onClick={() => mark(r.delegate_id, s)}
                            disabled={!sessionId}
                            className={`h-8 text-xs uppercase tracking-[0.18em] ${
                              status === s
                                ? "bg-[#0F4C81] text-white hover:bg-[#0A3860]"
                                : ""
                            }`}
                            data-testid={`mark-${s}-${r.delegate_id}`}
                          >
                            {s[0].toUpperCase() + s.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={3} className="text-center py-12 text-sm text-slate-500">
                  No delegates found for this event.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
